import { Box, Button, Flex, Text } from '@radix-ui/themes';
import type { AudienceCategoryItem } from '@digiseg-labs/api-client';
import type { CSSProperties } from 'react';
import { getAudienceColorStep, getCategoryTheme } from '../../utils/audienceBuilder';

type Props = {
  category: AudienceCategoryItem;
  selectedCodes: Set<string>;
  onToggle: (categoryId: string, code: string) => void;
};

const STACK_THRESHOLD = 18;

const AudienceBuilderCategory = ({
  category,
  selectedCodes,
  onToggle,
}: Props) => {
  const { color: categoryColor } = getCategoryTheme(category);
  const isActive = category.audiences.some((audience) => selectedCodes.has(audience.code));
  const categoryStyle = {
    '--category-color': `var(--${categoryColor}-9)`,
    '--category-color-soft': `var(--${categoryColor}-3)`,
    '--category-color-border': `var(--${categoryColor}-6)`,
  } as CSSProperties;

  const shouldStack = category.audiences.some((audience) => {
    const label = (audience.display_name ?? audience.code).replace(/\s+/g, '');
    return label.length > STACK_THRESHOLD;
  });

  return (
    <Box className="builder-category" data-active={isActive ? 'true' : 'false'} style={categoryStyle}>
      <Flex align="center" justify="between" className="builder-category-header">
        <Text size="2" weight="medium" className="builder-category-title">
          {category.display_name ?? category.code}
        </Text>
      </Flex>
      <Box className="builder-category-options" data-layout={shouldStack ? 'stack' : 'row'}>
        {category.audiences.map((audience) => {
          const isSelected = selectedCodes.has(audience.code);
          const swatchStep = getAudienceColorStep(audience.code);
          const buttonStyle = {
            '--audience-swatch-color': `var(--${categoryColor}-${swatchStep})`,
          } as CSSProperties;
          return (
            <Button
              key={audience.code}
              size="2"
              variant={isSelected ? 'solid' : 'soft'}
              color="gray"
              className={`builder-option-button${isSelected ? ' is-selected' : ''}`}
              style={buttonStyle}
              onClick={() => onToggle(category.code, audience.code)}
            >
              <span className="builder-option-label">{audience.display_name ?? audience.code}</span>
              {isSelected && <span className="builder-option-swatch" aria-hidden="true" />}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default AudienceBuilderCategory;
